import { useState, useRef } from 'react'
import { Box, Button, Card, CardContent, CardHeader, Typography, Alert, CircularProgress, Chip, FormControl, InputLabel, Select, MenuItem } from '@mui/material'
import { CloudUpload, Face, SentimentVerySatisfied } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/contexts/AuthContext'
import BarChart from '@/components/EmotionCharts/BarChart'
import PieChart from '@/components/EmotionCharts/PieChart'
import PlutchikChart from '@/components/EmotionCharts/PlutchikChart'
import { getApiBaseUrl } from '@/config/environment'

type ChartType = 'bar' | 'pie' | 'plutchik'

interface AnalysisResult {
  id: number
  face_detected: boolean
  dominant_emotion: string | null
  dominant_confidence: number | null
  emotions: Array<{ emotion: string; confidence: number }>
}

export default function ImageAnalysisPage() {
  const { t } = useTranslation('analysis')
  const { token } = useAuth()
  const [file, setFile] = useState<File | null>(null)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [result, setResult] = useState<AnalysisResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [chartType, setChartType] = useState<ChartType>('bar')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      setFile(selectedFile)
      setResult(null)
      setError(null)

      const reader = new FileReader()
      reader.onloadend = () => {
        setPreview(reader.result as string)
      }
      reader.readAsDataURL(selectedFile)
    }
  }

  const handleAnalyze = async () => {
    if (!file) return

    setIsAnalyzing(true)
    setError(null)

    try {
      const formData = new FormData()
      formData.append('image', file)

      if (!token) {
        setError(t('notAuthenticated'))
        return
      }

      const apiBaseUrl = getApiBaseUrl().replace(/\/+$/, '')
      const response = await fetch(`${apiBaseUrl}/analysis/image/analyze/`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      })

      if (!response.ok) {
        const errorData = await response.json()
        console.error('API Error:', errorData)

        if (response.status === 401) {
          throw new Error(t('authFailed'))
        }

        throw new Error(errorData.detail || errorData.error || t('analysisFailed'))
      }

      const data = await response.json()
      setResult(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('failedToAnalyze'))
    } finally {
      setIsAnalyzing(false)
    }
  }

  const handleReset = () => {
    setFile(null)
    setPreview(null)
    setResult(null)
    setError(null)
    setChartType('bar')
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const renderChart = () => {
    if (!result?.emotions) return null

    switch (chartType) {
      case 'bar':
        return <BarChart emotions={result.emotions} />
      case 'pie':
        return <PieChart emotions={result.emotions} />
      case 'plutchik':
        return <PlutchikChart emotions={result.emotions} />
      default:
        return <BarChart emotions={result.emotions} />
    }
  }

  const getChartTitle = () => {
    switch (chartType) {
      case 'bar':
        return t('barChart')
      case 'pie':
        return t('pieChart')
      case 'plutchik':
        return t('plutchikWheel')
      default:
        return t('barChart')
    }
  }

  const getEmotionColor = (emotion: string) => {
    const colors: Record<string, string> = {
      happy: '#22c55e',
      sad: '#3b82f6',
      angry: '#ef4444',
      fearful: '#8b5cf6',
      surprised: '#f59e0b',
      disgusted: '#a855f7',
      neutral: '#6b7280',
    }
    return colors[emotion] || '#6b7280'
  }

  const getEmotionLabel = (emotion: string) => {
    return emotion.charAt(0).toUpperCase() + emotion.slice(1)
  }

  return (
    <Box>
      <Card>
        <CardHeader title={t('emotionAnalysisTitle')} />
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png"
                onChange={handleFileChange}
                style={{ display: 'none' }}
                id="image-upload"
              />
              <label htmlFor="image-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUpload />}
                  fullWidth
                >
                  {file ? file.name : t('selectImage')}
                </Button>
              </label>
            </Box>

            {preview && !result?.face_detected && (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <img
                  src={preview}
                  alt={t('preview')}
                  style={{
                    maxWidth: '100%',
                    maxHeight: 400,
                    borderRadius: 8,
                    border: '2px solid #e5e7eb',
                  }}
                />
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                onClick={handleAnalyze}
                disabled={!file || isAnalyzing}
                startIcon={isAnalyzing ? <CircularProgress size={16} /> : <Face />}
              >
                {isAnalyzing ? t('analyzing') : t('analyzeEmotion')}
              </Button>
              <Button variant="outlined" onClick={handleReset} disabled={!file && !result}>
                {t('reset')}
              </Button>
            </Box>

            {error && (
              <Alert severity="error">{error}</Alert>
            )}

            {result && (
              <Box sx={{ mt: 2 }}>
                <Alert severity={result.face_detected ? 'success' : 'warning'} sx={{ mb: 2 }}>
                  {result.face_detected
                    ? t('faceDetectedDominant', {
                        emotion: result.dominant_emotion?.toUpperCase() ?? '',
                        confidence: (result.dominant_confidence! * 100).toFixed(1),
                      })
                    : t('noFaceDetected')}
                </Alert>

                {result.face_detected && (
                  <Box sx={{ mt: 3 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: 3,
                        alignItems: 'flex-start',
                      }}
                    >
                      {preview && (
                        <Box
                          sx={{
                            flex: { md: '0 0 42%' },
                            maxWidth: { md: '42%' },
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                          }}
                        >
                          <img
                            src={preview}
                            alt={t('analyzed')}
                            style={{
                              maxWidth: '100%',
                              maxHeight: 400,
                              borderRadius: 8,
                              border: '2px solid #e5e7eb',
                              objectFit: 'contain',
                            }}
                          />
                        </Box>
                      )}

                      <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between',
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            gap: 2,
                            mb: 2,
                          }}
                        >
                          <Typography variant="h6">
                            <SentimentVerySatisfied sx={{ mr: 1, verticalAlign: 'middle' }} />
                            {t('emotionBreakdown')}
                          </Typography>
                          <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>{t('chartType')}</InputLabel>
                            <Select
                              value={chartType}
                              label={t('chartType')}
                              onChange={(e) => setChartType(e.target.value as ChartType)}
                            >
                              <MenuItem value="bar">{t('barChart')}</MenuItem>
                              <MenuItem value="pie">{t('pieChart')}</MenuItem>
                              <MenuItem value="plutchik">{t('plutchikWheel')}</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>

                        <Card>
                          <CardHeader title={getChartTitle()} />
                          <CardContent>{renderChart()}</CardContent>
                        </Card>
                      </Box>
                    </Box>

                    <Box sx={{ mt: 3 }}>
                      <Typography variant="h6" gutterBottom>
                        {t('detailedBreakdown')}
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                        {result.emotions.map((emotion, index) => (
                          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Chip
                              label={getEmotionLabel(emotion.emotion)}
                              sx={{
                                backgroundColor: getEmotionColor(emotion.emotion),
                                color: 'white',
                                fontWeight: 'bold',
                                minWidth: 100,
                              }}
                            />
                            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box
                                sx={{
                                  height: 8,
                                  borderRadius: 4,
                                  backgroundColor: getEmotionColor(emotion.emotion),
                                  width: `${emotion.confidence * 100}%`,
                                  transition: 'width 0.3s ease',
                                }}
                              />
                              <Typography variant="body2" color="text.secondary">
                                {(emotion.confidence * 100).toFixed(1)}%
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  )
}
